import React, { useEffect, useMemo, useRef, useState } from "react";
import { alpha } from "@mui/material/styles";
import {
  Box,
  Checkbox,
  Chip,
  Collapse,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputAdornment,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ContactPhoneOutlinedIcon from "@mui/icons-material/ContactPhoneOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import EventSeatIcon from "@mui/icons-material/EventSeat";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import BPePassengerClientBar from "./BPePassengerClientBar";
import { birthDateFieldText, completeBirthDateIso, maskBirthDateTyping } from "../../utils/bpeBirthDateInput";

function formatSeatLabel(seat) {
  if (seat == null || seat === "") return null;
  return String(seat).padStart(2, "0");
}

function formatDocumentInput(value) {
  const digits = String(value || "").replace(/\D/g, "").slice(0, 14);
  if (digits.length <= 11) {
    return digits
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d{1,2})$/, "$1-$2");
  }
  return digits.slice(0, 14);
}

function formatPhoneInput(value) {
  const digits = String(value || "").replace(/\D/g, "").slice(0, 11);
  if (digits.length <= 10) {
    return digits.replace(/(\d{2})(\d)/, "($1) $2").replace(/(\d{4})(\d)/, "$1-$2");
  }
  return digits.replace(/(\d{2})(\d)/, "($1) $2").replace(/(\d{5})(\d)/, "$1-$2");
}

function isPassengerComplete(p, categories = [], requireBirthDate = false) {
  if (!p?.nome?.trim() || !p?.documento?.trim()) return false;
  const meta = categories.find((c) => c.id === (p.categoria || "geral"));
  if (meta?.requiresDeclaration && !p.declaracaoDoc) return false;
  if ((requireBirthDate || meta?.requiresBirthDate) && !completeBirthDateIso(p.dataNascimento)) return false;
  if (meta?.requiresIncome && (p.rendaMensal == null || String(p.rendaMensal).trim() === "")) {
    return false;
  }
  return true;
}

/**
 * Passo de dados dos passageiros — formulário compacto para o balcão.
 */
export default function BPePassengerStep({
  passageiros,
  assentosSelected,
  activePassengerIndex,
  onActivePassengerChange,
  onChange,
  passengerCategories = [],
  requireBirthDate = false,
  hasClientPrefill,
  canSyncRegistry,
  showValidation,
  requestClient,
  relatedClients,
  onApplyRequestClient,
  onApplyRelatedClient,
  onClearPassenger,
  syncRegistry,
  onSyncRegistryChange,
  t,
}) {
  const nomeRef = useRef(null);
  const [contactOpen, setContactOpen] = useState({});

  const multiPassenger = passageiros.length > 1;

  const completedCount = useMemo(
    () => passageiros.filter((p) => isPassengerComplete(p, passengerCategories, requireBirthDate)).length,
    [passageiros, passengerCategories, requireBirthDate]
  );
  const allDone = completedCount === passageiros.length;
  const progressPct = passageiros.length > 0 ? (completedCount / passageiros.length) * 100 : 0;

  const firstMissing = useMemo(() => {
    const idx = passageiros.findIndex((p) => !isPassengerComplete(p, passengerCategories, requireBirthDate));
    return idx;
  }, [passageiros, passengerCategories, requireBirthDate]);

  useEffect(() => {
    nomeRef.current?.focus();
  }, [activePassengerIndex]);

  useEffect(() => {
    passageiros.forEach((p, i) => {
      if (p.email?.trim() || p.celular?.trim()) {
        setContactOpen((prev) => ({ ...prev, [i]: true }));
      }
    });
  }, [passageiros]);

  const handleFieldChange = (index, field, value) => {
    let next = value;
    if (field === "documento") next = formatDocumentInput(value);
    if (field === "celular") next = formatPhoneInput(value);
    onChange(index, field, next);
  };

  const tryAdvancePassenger = (index) => {
    const p = passageiros[index];
    if (!multiPassenger || !isPassengerComplete(p, passengerCategories, requireBirthDate)) return;
    const nextMissing = passageiros.findIndex(
      (item, idx) => idx !== index && !isPassengerComplete(item, passengerCategories, requireBirthDate)
    );
    if (nextMissing >= 0) onActivePassengerChange(nextMissing);
  };

  const categories =
    passengerCategories.length > 0
      ? passengerCategories
      : [{ id: "geral", label: "Passageiro em geral (tarifa integral)", requiresDeclaration: false }];

  const renderPassengerForm = (index) => {
    const p = passageiros[index];
    const seat = formatSeatLabel(assentosSelected[index]);
    const done = isPassengerComplete(p, passengerCategories, requireBirthDate);
    const isActive = activePassengerIndex === index;
    if (multiPassenger && !isActive) return null;

    const contactExpanded = contactOpen[index] ?? Boolean(p.email?.trim() || p.celular?.trim());
    const categoryMeta = categories.find((c) => c.id === (p.categoria || "geral"));
    const birthRequired = Boolean(requireBirthDate) || Boolean(categoryMeta?.requiresBirthDate);

    return (
      <Paper
        key={index}
        variant="outlined"
        sx={{
          p: { xs: 1.25, sm: 1.5 },
          borderRadius: 2,
          borderColor: done ? "success.light" : "divider",
          bgcolor: done ? (theme) => alpha(theme.palette.success.main, 0.03) : "background.paper",
        }}
      >
        <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" sx={{ mb: 1 }}>
          {done ? (
            <CheckCircleIcon color="success" sx={{ fontSize: 18 }} />
          ) : (
            <PersonOutlineIcon color="primary" sx={{ fontSize: 18 }} />
          )}
          <Typography variant="body2" fontWeight={700}>
            {t("bpeWizard.passengerN", { n: index + 1 })}
          </Typography>
          {seat && (
            <Chip
              size="small"
              icon={<EventSeatIcon />}
              label={t("bpeWizard.passengerSeatChip", { seat })}
              color="primary"
              variant="outlined"
              sx={{ height: 22, fontWeight: 700 }}
            />
          )}
        </Stack>

        {hasClientPrefill && (
          <BPePassengerClientBar
            passengerIndex={index}
            passenger={p}
            requestClient={requestClient}
            relatedOptions={relatedClients}
            onApplyRequestClient={onApplyRequestClient}
            onApplyRelatedClient={onApplyRelatedClient}
            onClear={() => onClearPassenger(index)}
            t={t}
          />
        )}

        <FormControl fullWidth size="small" sx={{ mb: 1 }}>
          <InputLabel id={`bpe-passenger-cat-${index}`}>{t("bpeWizard.passengerCategory")}</InputLabel>
          <Select
            labelId={`bpe-passenger-cat-${index}`}
            label={t("bpeWizard.passengerCategory")}
            value={p.categoria || "geral"}
            onChange={(e) => {
              onChange(index, "categoria", e.target.value);
              onChange(index, "declaracaoDoc", false);
            }}
          >
            {categories.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.label}
                {c.qtdDisponivel != null && c.qtdDisponivel > 0
                  ? ` (${c.qtdDisponivel})`
                  : null}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>{t("bpeWizard.passengerCategoryHint")}</FormHelperText>
        </FormControl>

        {categoryMeta?.requiresDeclaration ? (
          <Paper
            variant="outlined"
            sx={{
              p: 1.25,
              mb: 1,
              borderRadius: 1.5,
              borderColor: "warning.light",
              bgcolor: (theme) => alpha(theme.palette.warning.main, 0.06),
            }}
          >
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.75 }}>
              {t("bpeWizard.passengerBenefitDocHint", { label: categoryMeta.label })}
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={!!p.declaracaoDoc}
                  onChange={(e) => onChange(index, "declaracaoDoc", e.target.checked)}
                />
              }
              label={
                <Typography variant="caption">{t("bpeWizard.passengerBenefitDeclaration")}</Typography>
              }
              sx={{ ml: 0, alignItems: "flex-start" }}
            />
            {showValidation && isActive && !p.declaracaoDoc ? (
              <FormHelperText error sx={{ ml: 0 }}>
                {t("bpeWizard.passengerBenefitDeclarationRequired")}
              </FormHelperText>
            ) : null}
          </Paper>
        ) : null}

        {(birthRequired || categoryMeta?.requiresIncome) && (
          <Grid container spacing={1} sx={{ mb: 1 }}>
            {birthRequired ? (
              <Grid item xs={12} sm={categoryMeta?.requiresIncome ? 6 : 12}>
                <TextField
                  label={t("bpeWizard.passengerBirthDate")}
                  value={birthDateFieldText(p.dataNascimento)}
                  onChange={(e) => {
                    const masked = maskBirthDateTyping(e.target.value);
                    onChange(index, "dataNascimento", completeBirthDateIso(masked) || masked);
                  }}
                  placeholder="dd/mm/aaaa"
                  fullWidth
                  size="small"
                  required
                  autoComplete="off"
                  inputProps={{ inputMode: "numeric", autoComplete: "off" }}
                  InputLabelProps={{ shrink: true }}
                  error={showValidation && isActive && !completeBirthDateIso(p.dataNascimento)}
                  helperText={
                    showValidation && isActive && !completeBirthDateIso(p.dataNascimento)
                      ? requireBirthDate && !categoryMeta?.requiresBirthDate
                        ? t("bpeWizard.passengerBirthDateRequiredTrip")
                        : t("bpeWizard.passengerBirthDateRequired")
                      : " "
                  }
                />
              </Grid>
            ) : null}
            {categoryMeta?.requiresIncome ? (
              <Grid item xs={12} sm={6}>
                <TextField
                  label={t("bpeWizard.passengerMonthlyIncome")}
                  type="number"
                  inputProps={{ min: 0, step: "0.01" }}
                  value={p.rendaMensal ?? ""}
                  onChange={(e) => onChange(index, "rendaMensal", e.target.value)}
                  fullWidth
                  size="small"
                  required
                  error={
                    showValidation &&
                    isActive &&
                    (p.rendaMensal == null || String(p.rendaMensal).trim() === "")
                  }
                  helperText={
                    showValidation &&
                    isActive &&
                    (p.rendaMensal == null || String(p.rendaMensal).trim() === "")
                      ? t("bpeWizard.passengerIncomeRequired")
                      : " "
                  }
                />
              </Grid>
            ) : null}
          </Grid>
        )}

        <Grid container spacing={1}>
          <Grid item xs={12} sm={7}>
            <TextField
              inputRef={isActive ? nomeRef : undefined}
              label={t("bpeWizard.name")}
              value={p.nome}
              onChange={(e) => handleFieldChange(index, "nome", e.target.value)}
              fullWidth
              size="small"
              required
              error={showValidation && isActive && !p.nome?.trim()}
              helperText={
                showValidation && isActive && !p.nome?.trim()
                  ? t("bpeWizard.passengerNameRequired")
                  : " "
              }
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonOutlineIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={5}>
            <TextField
              label={t("bpeWizard.document")}
              value={p.documento}
              onChange={(e) => handleFieldChange(index, "documento", e.target.value)}
              fullWidth
              size="small"
              required
              error={showValidation && isActive && !p.documento?.trim()}
              helperText={
                showValidation && isActive && !p.documento?.trim()
                  ? t("bpeWizard.passengerDocumentRequired")
                  : t("bpeWizard.documentHelper")
              }
              placeholder="000.000.000-00"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <BadgeOutlinedIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
              }}
              onBlur={() => tryAdvancePassenger(index)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  tryAdvancePassenger(index);
                }
              }}
            />
          </Grid>
        </Grid>

        <Box sx={{ mt: 0.5 }}>
          <Stack
            direction="row"
            spacing={0.5}
            alignItems="center"
            onClick={() => setContactOpen((prev) => ({ ...prev, [index]: !contactExpanded }))}
            sx={{
              cursor: "pointer",
              py: 0.75,
              userSelect: "none",
              width: "fit-content",
            }}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setContactOpen((prev) => ({ ...prev, [index]: !contactExpanded }));
              }
            }}
          >
            {contactExpanded ? (
              <ExpandLessIcon fontSize="small" color="action" />
            ) : (
              <ExpandMoreIcon fontSize="small" color="action" />
            )}
            <Typography variant="caption" fontWeight={700} color="text.secondary">
              {t("bpeWizard.passengerContactOptional")}
            </Typography>
          </Stack>
          <Collapse in={contactExpanded}>
            <Grid container spacing={1.5} sx={{ mt: 0.25 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={t("bpeWizard.email")}
                  type="email"
                  value={p.email}
                  onChange={(e) => handleFieldChange(index, "email", e.target.value)}
                  fullWidth
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailOutlinedIcon fontSize="small" color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={t("bpeWizard.phone")}
                  value={p.celular}
                  onChange={(e) => handleFieldChange(index, "celular", e.target.value)}
                  fullWidth
                  size="small"
                  placeholder="(00) 00000-0000"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <ContactPhoneOutlinedIcon fontSize="small" color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          </Collapse>
        </Box>
      </Paper>
    );
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.75 }}>
        <Typography variant="caption" color="text.secondary" fontWeight={600}>
          {multiPassenger
            ? t(requireBirthDate ? "bpeWizard.passengerDataHintMultiBirth" : "bpeWizard.passengerDataHintMulti")
            : t(requireBirthDate ? "bpeWizard.passengerDataHintSingleBirth" : "bpeWizard.passengerDataHintSingle")}
        </Typography>
        <Chip
          size="small"
          label={t(requireBirthDate ? "bpeWizard.passengerRequiredFieldsBirth" : "bpeWizard.passengerRequiredFields")}
          variant="outlined"
          sx={{ height: 24 }}
        />
      </Stack>

      {multiPassenger && (
        <Paper variant="outlined" sx={{ p: 1, mb: 1, borderRadius: 1.5 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
            <Typography variant="caption" fontWeight={700} color="text.secondary">
              {t("bpeWizard.passengerProgress", { done: completedCount, total: passageiros.length })}
            </Typography>
            {allDone && (
              <Typography variant="caption" color="success.main" fontWeight={700}>
                {t("bpeWizard.passengerAllDone")}
              </Typography>
            )}
          </Stack>
          <LinearProgress
            variant="determinate"
            value={progressPct}
            color={allDone ? "success" : "primary"}
            sx={{ height: 6, borderRadius: 1 }}
          />
        </Paper>
      )}

      {multiPassenger && (
        <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mb: 1 }} role="tablist">
          {passageiros.map((p, i) => {
            const done = isPassengerComplete(p, passengerCategories, requireBirthDate);
            const isActive = activePassengerIndex === i;
            const isMissing = !done && i === firstMissing;
            const seat = formatSeatLabel(assentosSelected[i]);
            return (
              <Paper
                key={i}
                component="button"
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => onActivePassengerChange(i)}
                variant="outlined"
                sx={{
                  p: 1,
                  minWidth: 108,
                  flex: "1 1 108px",
                  maxWidth: 170,
                  textAlign: "left",
                  cursor: "pointer",
                  borderRadius: 2,
                  borderWidth: isActive ? 2 : 1,
                  borderColor: isActive ? "primary.main" : done ? "success.light" : "divider",
                  bgcolor: isActive
                    ? (theme) => alpha(theme.palette.primary.main, 0.08)
                    : "background.paper",
                  outline: isMissing && !isActive ? "2px dashed" : "none",
                  outlineColor: "warning.main",
                  outlineOffset: 2,
                  "&:hover": {
                    borderColor: "primary.main",
                  },
                }}
              >
                <Typography variant="caption" color="text.secondary" display="block" lineHeight={1.2}>
                  {t("bpeWizard.passengerN", { n: i + 1 })}
                </Typography>
                <Typography variant="body2" fontWeight={700} lineHeight={1.2} noWrap>
                  {done
                    ? p.nome.trim().split(" ")[0]
                    : seat
                      ? t("bpeWizard.passengerSeatChip", { seat })
                      : t("bpeWizard.passengerPending")}
                </Typography>
              </Paper>
            );
          })}
        </Stack>
      )}

      {passageiros.map((_, i) => renderPassengerForm(i))}

      {canSyncRegistry && (
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={syncRegistry}
              onChange={(e) => onSyncRegistryChange(e.target.checked)}
            />
          }
          label={
            <Typography variant="caption" color="text.secondary">
              {hasClientPrefill
                ? t("bpeWizard.passengerSyncRegistry")
                : t("bpeWizard.passengerSyncRegistryCreate")}
            </Typography>
          }
          sx={{ mt: 1, ml: 0, alignItems: "center" }}
        />
      )}
    </Box>
  );
}
